import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Calendar } from '../components/ui/calendar';
import { ChevronLeft, ChevronRight, MapPin, Clock, Tag } from 'lucide-react';
import axios from 'axios';
import { format, parseISO, isSameDay, startOfMonth, endOfMonth, isSameMonth } from 'date-fns';
import { es } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const eventTypeColors = {
  entrenamiento: 'bg-blue-500',
  partido: 'bg-orange-500',
  torneo: 'bg-amber-500',
  reunion: 'bg-green-500',
  otro: 'bg-zinc-500'
};

export function CalendarSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [month, setMonth] = useState(new Date());

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data } = await axios.get(`${API}/api/events`);
      setEvents(data);
    } catch (error) {
      console.log('No events loaded');
    }
  };

  const eventsOnDate = (date) => {
    return events.filter(event => {
      try {
        return isSameDay(parseISO(event.date), date);
      } catch {
        return false;
      }
    });
  };

  const selectedDateEvents = eventsOnDate(selectedDate);

  const upcomingEvents = events
    .filter(event => {
      try {
        return parseISO(event.date) >= new Date();
      } catch {
        return false;
      }
    })
    .slice(0, 5);

  return (
    <section 
      id="calendario" 
      className="py-24 px-4 sm:px-6 lg:px-8 bg-zinc-950"
      ref={ref}
      data-testid="calendar-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="text-orange-500 font-semibold tracking-widest text-sm">AGENDA DEPORTIVA</span>
          <h2 
            className="text-4xl sm:text-5xl font-extrabold text-white mt-2 mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="calendar-title"
          >
            CALENDARIO DE EVENTOS
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Mantente al día con nuestras actividades, entrenamientos y competencias.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <motion.div
            className="lg:col-span-2 bg-zinc-900/50 border border-white/10 rounded-xl p-6"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={month}
              onMonthChange={setMonth}
              locale={es}
              className="w-full"
              modifiers={{
                hasEvent: (date) => eventsOnDate(date).length > 0
              }}
              modifiersStyles={{
                hasEvent: {
                  backgroundColor: 'rgba(234, 88, 12, 0.3)',
                  borderRadius: '50%'
                }
              }}
              data-testid="calendar-widget"
            />

            {/* Selected date events */}
            {selectedDateEvents.length > 0 && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                  EVENTOS DEL {format(selectedDate, "d 'de' MMMM", { locale: es }).toUpperCase()}
                </h3>
                <div className="space-y-3">
                  {selectedDateEvents.map(event => (
                    <div 
                      key={event.id} 
                      className="bg-zinc-800/50 rounded-lg p-4 border-l-4 border-orange-500"
                      data-testid={`event-${event.id}`}
                    >
                      <h4 className="font-semibold text-white">{event.title}</h4>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-zinc-400">
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {event.time}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {event.location}
                        </span>
                      </div>
                      {event.description && (
                        <p className="text-zinc-400 text-sm mt-2">{event.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {/* Upcoming Events */}
          <motion.div
            className="bg-zinc-900/50 border border-white/10 rounded-xl p-6"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 
              className="text-xl font-bold text-white mb-6"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              PRÓXIMOS EVENTOS
            </h3>

            {upcomingEvents.length > 0 ? (
              <div className="space-y-4">
                {upcomingEvents.map(event => (
                  <div 
                    key={event.id}
                    className="p-4 bg-zinc-800/30 rounded-lg border border-white/5 hover:border-orange-500/30 transition-colors cursor-pointer"
                    onClick={() => {
                      try {
                        setSelectedDate(parseISO(event.date));
                        setMonth(parseISO(event.date));
                      } catch {}
                    }}
                    data-testid={`upcoming-event-${event.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-12 text-center">
                        <div className="text-2xl font-bold text-orange-500">
                          {format(parseISO(event.date), 'd', { locale: es })}
                        </div>
                        <div className="text-xs text-zinc-500 uppercase">
                          {format(parseISO(event.date), 'MMM', { locale: es })}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-white truncate">{event.title}</h4>
                        <div className="flex items-center gap-2 mt-1 text-xs text-zinc-400">
                          <Clock size={12} />
                          <span>{event.time}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-block w-2 h-2 rounded-full ${eventTypeColors[event.event_type] || eventTypeColors.otro}`} />
                          <span className="text-xs text-zinc-500 capitalize">{event.event_type}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-zinc-500">No hay eventos programados</p>
              </div>
            )}

            {/* Legend */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <p className="text-xs text-zinc-500 mb-3">Tipos de eventos:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(eventTypeColors).map(([type, color]) => (
                  <span key={type} className="flex items-center gap-1 text-xs text-zinc-400">
                    <span className={`w-2 h-2 rounded-full ${color}`} />
                    <span className="capitalize">{type}</span>
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
